import gql from 'graphql-tag'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useApolloClient } from 'react-apollo'
import { formRawWallClock } from '../api/events/helpers'

// The add-event form's "possible duplicate" check.
//   * The start is sent as the wall-clock time typed in the form (formRawWallClock, the same
//     value saving submits), with the venue, community and a typed-in venue's city and state.
//     The server reads it in the zone saving would use, so the computer's own zone never
//     matters. An all-day event says so; the server starts it at the beginning of its day.
//     startAt (the start as an instant on this computer) is still sent for older servers; the
//     server ignores it when startAtLocal is given.
//   * Only the answer to the most recent check is shown: each check takes a sequence number,
//     and an answer to a superseded check (one sent before any later change, even one still
//     waiting out the typing pause) is ignored.
export const CheckEventDuplicatesQuery = gql`
  query CheckEventDuplicates(
    $title: String!
    $startAt: ISO8601DateTime
    $startAtLocal: String
    $allDay: Boolean
    $placeId: ID
    $primaryCommunityId: ID
    $tempPlaceAddressState: String
    $tempPlaceAddressCity: String
    $placeTitle: String
    $zipCode: String
    $externalUrl: String
  ) {
    checkEventDuplicates(
      title: $title
      startAt: $startAt
      startAtLocal: $startAtLocal
      allDay: $allDay
      placeId: $placeId
      primaryCommunityId: $primaryCommunityId
      tempPlaceAddressState: $tempPlaceAddressState
      tempPlaceAddressCity: $tempPlaceAddressCity
      placeTitle: $placeTitle
      zipCode: $zipCode
      externalUrl: $externalUrl
    ) {
      id
      title
      startAt
      timeZone
      placeTitle
      similarityScore
      similarityReasons
      eventUrl
    }
  }
`

export type PotentialDuplicate = {
  id: string
  title: string
  startAt: string | null
  timeZone: string | null
  placeTitle: string | null
  similarityScore: number
  similarityReasons: string[]
  eventUrl: string
}

type UseDuplicateCheckParams = {
  title: string
  startAt?: string | Date | null
  startAtDate?: Date | null
  startAtTime?: string | null
  allDay?: boolean | null
  placeId?: string | null
  primaryCommunityId?: string | null
  tempPlaceAddressState?: string | null
  tempPlaceAddressCity?: string | null
  placeTitle?: string
  zipCode?: string
  externalUrl?: string
  // False turns the check off (the edit page, which shows no duplicate warning).
  enabled?: boolean
}

type UseDuplicateCheckResult = {
  potentialDuplicates: PotentialDuplicate[]
  isChecking: boolean
  checkForDuplicates: () => void
  clearResults: () => void
}

const DEBOUNCE_DELAY = 500

const useDuplicateCheck = (params: UseDuplicateCheckParams): UseDuplicateCheckResult => {
  const { title, startAt, startAtDate, startAtTime, allDay, placeId, primaryCommunityId } = params
  const { tempPlaceAddressState, tempPlaceAddressCity, placeTitle, zipCode, externalUrl, enabled = true } = params
  const [potentialDuplicates, setPotentialDuplicates] = useState<PotentialDuplicate[]>([])
  const [isChecking, setIsChecking] = useState(false)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestSeqRef = useRef(0)
  const client = useApolloClient()

  const { startAtLocal } = formRawWallClock({
    startAtDate: startAtDate ?? undefined,
    startAtTime: startAtTime ?? undefined,
  })
  const canCheck = Boolean(enabled && title && title.length >= 3 && startAtLocal)

  const variables = {
    title,
    startAt: startAt instanceof Date ? startAt.toISOString() : startAt || null,
    startAtLocal: startAtLocal ?? null,
    allDay: Boolean(allDay),
    placeId: placeId || null,
    primaryCommunityId: primaryCommunityId || null,
    tempPlaceAddressState: tempPlaceAddressState || null,
    tempPlaceAddressCity: tempPlaceAddressCity || null,
    placeTitle: placeTitle || null,
    zipCode: zipCode || null,
    externalUrl: externalUrl || null,
  }
  // Every input the check depends on; a change re-runs the (debounced) check.
  const key = JSON.stringify(variables)

  const runCheck = useCallback(() => {
    const seq = ++requestSeqRef.current
    setIsChecking(true)
    client
      .query<{ checkEventDuplicates?: PotentialDuplicate[] }>({
        query: CheckEventDuplicatesQuery,
        fetchPolicy: 'network-only',
        variables: JSON.parse(key),
        // Optional check: a failure is logged, not shown
        context: { silentErrors: true },
      })
      .then(({ data }) => {
        if (seq !== requestSeqRef.current) return
        setIsChecking(false)
        if (data?.checkEventDuplicates) {
          setPotentialDuplicates(data.checkEventDuplicates)
        }
      })
      .catch(error => {
        if (seq !== requestSeqRef.current) return
        setIsChecking(false)
        // Log error but don't show to user - duplicate check is a non-critical feature
        console.error('Duplicate check failed:', error.message)
      })
  }, [client, key])

  const checkForDuplicates = useCallback(() => {
    if (canCheck) runCheck()
  }, [canCheck, runCheck])

  const clearResults = useCallback(() => {
    setPotentialDuplicates([])
  }, [])

  // An answer arriving after unmount is ignored.
  useEffect(() => () => {
    requestSeqRef.current++
  }, [])

  // Debounced auto-check whenever the title, start, venue or community changes.
  // Any change supersedes a check still in flight, so its late answer is ignored, even while the
  // new check waits out the typing pause.
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    requestSeqRef.current++

    if (!canCheck) {
      setIsChecking(false)
      setPotentialDuplicates([])
      return
    }

    debounceTimerRef.current = setTimeout(runCheck, DEBOUNCE_DELAY)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [canCheck, runCheck])

  return {
    potentialDuplicates,
    isChecking,
    checkForDuplicates,
    clearResults,
  }
}

export default useDuplicateCheck
